'use strict';

const storage = require('./storage');
const { uuid, now, hasAnyParam, throttle, safe, clean, scrubUrl } = require('./utils');
const { createTransport } = require('./transport');

const KEYS = {
  visitor: '_attr_vid',
  session: '_attr_sid',
  sessionExpiry: '_attr_sexp',
  attribution: '_attr_first',
  user: '_attr_uid',
  metadata: '_attr_meta',
  queue: '_attr_q',
  config: '_attr_cfg',
  identified: '_attr_idk',
  sessionTouch: '_attr_stouch',
};

const FALLBACK_TRIGGERS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'ttclid', 'msclkid', 'twclid', 'li_fat_id',
  'aff_id', 'affiliate_id', 'partner', 'campaign',
  'clickid', 'click_id', 'subid', 'sub_id', 'cid', 'source',
];

const DOWNLOAD_EXT = /\.(pdf|zip|rar|7z|tar|gz|dmg|exe|msi|pkg|deb|apk|csv|xlsx?|docx?|pptx?|mp3|mp4|mov|avi)(\?|$)/i;

function createTracker() {
  const state = {
    initialized: false,
    active: false,
    config: null,
    options: {},
    visitorId: null,
    sessionId: null,
    userId: null,
    metadata: {},
    transport: null,
    lastPath: null,
    maxScroll: 0,
    startedAt: Date.now(),
    lateArmed: false,
    lateActivating: false,
    apiHost: null,
    listeners: [],
    // Resolves when init() finishes (activate or idle). Lets identify/login
    // that fire during async config load wait instead of being dropped.
    ready: null,
    resolveReady: null,
    // One-shot flags — avoid stacking .then() handlers or duplicate emits.
    pendingIdentify: false,
    pendingLogin: false,
    pendingRegister: false,
  };

  function markReady() {
    if (typeof state.resolveReady === 'function') {
      const resolve = state.resolveReady;
      state.resolveReady = null;
      resolve(api);
    }
  }

  function rememberTraits(traits) {
    if (!traits || typeof traits !== 'object') return;
    state.metadata = Object.assign({}, state.metadata || {}, traits);
    storage.jsonSet(KEYS.metadata, state.metadata);
  }

  // Event-level fields on login/register that describe the action, not the person.
  const NON_TRAIT_KEYS = ['method'];

  function setIdentity(userId, traits) {
    state.userId = String(userId);
    storage.lsSet(KEYS.user, state.userId);
    if (!traits || typeof traits !== 'object') return;
    const personTraits = {};
    Object.keys(traits).forEach((key) => {
      if (NON_TRAIT_KEYS.indexOf(key) === -1) personTraits[key] = traits[key];
    });
    rememberTraits(personTraits);
  }

  function identityKey(props) {
    return state.sessionId + '|' + state.userId + '|' + (safe(() => JSON.stringify(props || {}))() || '');
  }

  /**
   * SPAs call identify() on every boot and next to login()/register(); one
   * identify per session per user+traits is enough to link the journey.
   */
  function emitIdentify(traits) {
    const props = traits || state.metadata || {};
    const key = identityKey(props);
    if (storage.ssGet(KEYS.identified) === key) return null;
    storage.ssSet(KEYS.identified, key);
    return track('identify', props, { flush: true });
  }

  /** login/register already carry userId + traits, so they satisfy identify for this session. */
  function trackIdentityEvent(name, properties) {
    const props = properties || state.metadata || {};
    if (state.userId) storage.ssSet(KEYS.identified, identityKey(props));
    return track(name, props, { flush: true });
  }

  /** Flush at most one deferred identity call after activate — no extra load. */
  function flushPendingIdentity() {
    if (!state.active) return;
    if (state.pendingLogin || state.pendingRegister) state.pendingIdentify = false;
    if (state.pendingIdentify && state.userId) {
      state.pendingIdentify = false;
      emitIdentify(state.metadata || {});
    } else {
      state.pendingIdentify = false;
    }
    if (state.pendingLogin) {
      state.pendingLogin = false;
      trackIdentityEvent('login', state.metadata || {});
    }
    if (state.pendingRegister) {
      state.pendingRegister = false;
      trackIdentityEvent('register', state.metadata || {});
    }
  }

  // ---------------------------------------------------------------- context
  function screenInfo() {
    const s = window.screen || {};
    return { width: s.width || null, height: s.height || null, pixelRatio: window.devicePixelRatio || 1, colorDepth: s.colorDepth || null };
  }

  function viewportInfo() {
    return { width: window.innerWidth || null, height: window.innerHeight || null };
  }

  const currentUrl = () => scrubUrl(window.location.href);
  const currentReferrer = () => scrubUrl(document.referrer) || null;

  /**
   * The touch that started this session (landing URL + the referrer seen on
   * that page). The server attributes every batch to it, so it must not drift
   * to later pages (an OAuth return would otherwise read as a new channel)
   * nor stick to the visitor's first-ever landing (new campaigns would be lost).
   */
  function sessionTouch() {
    const stored = storage.jsonGet(KEYS.sessionTouch);
    return stored && stored.sessionId === state.sessionId ? stored : null;
  }

  function captureSessionTouch(force) {
    const existing = sessionTouch();
    if (existing && !force) return existing;
    const touch = { sessionId: state.sessionId, landingUrl: currentUrl(), referrer: currentReferrer() };
    storage.jsonSet(KEYS.sessionTouch, touch);
    return touch;
  }

  /** A campaign URL reached mid-session (SPA nav, new ad click) starts a new touch. */
  function refreshSessionTouchIfCampaign() {
    if (!hasAnyParam(window.location.search, triggerParams())) return;
    const existing = sessionTouch();
    if (!existing || existing.landingUrl !== currentUrl()) captureSessionTouch(true);
  }

  function context() {
    const touch = sessionTouch();
    return clean({
      url: currentUrl(),
      landingUrl: (touch && touch.landingUrl) || currentUrl(),
      referrer: touch ? touch.referrer : currentReferrer(),
      language: navigator.language || null,
      timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone)() || null,
      screen: screenInfo(),
      viewport: viewportInfo(),
      userId: state.userId || null,
      traits: Object.keys(state.metadata).length ? state.metadata : null,
    });
  }

  // ---------------------------------------------------------------- session
  function idleTimeoutMs() {
    const minutes = (state.config && state.config.session && state.config.session.idleTimeoutMin) || 30;
    return minutes * 60 * 1000;
  }

  function ensureSession() {
    const stored = storage.ssGet(KEYS.session) || storage.lsGet(KEYS.session);
    const expiry = Number(storage.lsGet(KEYS.sessionExpiry) || 0);
    const expired = !stored || !expiry || Date.now() > expiry;

    if (expired) {
      state.sessionId = 's_' + uuid().replace(/-/g, '');
      storage.ssSet(KEYS.session, state.sessionId);
      storage.lsSet(KEYS.session, state.sessionId);
      touchSession();
      return { sessionId: state.sessionId, isNew: true };
    }
    state.sessionId = stored;
    touchSession();
    return { sessionId: state.sessionId, isNew: false };
  }

  function touchSession() {
    storage.lsSet(KEYS.sessionExpiry, String(Date.now() + idleTimeoutMs()));
  }

  // ------------------------------------------------------------ attribution
  function triggerParams() {
    return (state.config && state.config.triggerParams) || state.options.triggerParams || FALLBACK_TRIGGERS;
  }

  /**
   * Tracking only starts when a UTM / click-id / attribution param is present
   * on the URL, OR when this browser was already attributed on an earlier visit.
   * Organic traffic with no attribution signal → the SDK does nothing at all
   * (no network, no listeners, no timers).
   */
  function shouldActivate() {
    if (state.options.force === true) return true;
    if (hasAnyParam(window.location.search, triggerParams())) return true;
    return Boolean(storage.jsonGet(KEYS.attribution));
  }

  function persistFirstTouch() {
    const existing = storage.jsonGet(KEYS.attribution);
    // First touch is written once and never overwritten.
    if (existing) return existing;
    const touch = {
      landingUrl: currentUrl(),
      referrer: currentReferrer(),
      at: now(),
    };
    storage.jsonSet(KEYS.attribution, touch);
    return touch;
  }

  // ------------------------------------------------------------------ core
  function track(name, properties, options) {
    if (!state.active || !state.transport) return null;
    try {
      touchSession();
      const event = clean({
        eventId: 'e_' + uuid().replace(/-/g, ''),
        name: String(name).toLowerCase(),
        visitorId: state.visitorId,
        sessionId: state.sessionId,
        url: currentUrl(),
        title: document.title || null,
        referrer: currentReferrer(),
        userId: state.userId || null,
        revenue: options && options.revenue,
        currency: options && options.currency,
        // Traits ride on context.traits once per batch, not on every event.
        properties: Object.assign({}, properties || {}),
        occurredAt: now(),
      });
      state.transport.enqueue(event, context());
      if (options && options.flush) state.transport.flush(context(), Boolean(options.immediate));
      return event.eventId;
    } catch (_) {
      return null;
    }
  }

  // -------------------------------------------------------------- listeners
  function on(target, type, handler, opts) {
    target.addEventListener(type, handler, opts || false);
    state.listeners.push(function off() { target.removeEventListener(type, handler, opts || false); });
  }

  function bindAutomaticTracking() {
    const features = (state.config && state.config.features) || {};

    // ---- SPA navigation
    const emitPageView = () => {
      const path = window.location.pathname + window.location.search;
      if (path === state.lastPath) return;
      state.lastPath = path;
      state.maxScroll = 0;
      refreshSessionTouchIfCampaign();
      // path/query already live on the event's url + path columns.
      track('page_view', {});
    };

    ['pushState', 'replaceState'].forEach((method) => {
      try {
        const original = history[method];
        if (typeof original !== 'function' || original.__attrPatched) return;
        const patched = function patchedHistory() {
          const result = original.apply(this, arguments);
          setTimeout(safe(emitPageView), 0);
          return result;
        };
        patched.__attrPatched = true;
        history[method] = patched;
      } catch (_) { /* never break host SPA routers */ }
    });
    on(window, 'popstate', emitPageView);
    on(window, 'hashchange', emitPageView);

    // ---- clicks, outbound links, downloads
    if (features.trackClicks !== false || features.trackOutbound !== false) {
      on(document, 'click', (evt) => {
        const anchor = evt.target && evt.target.closest ? evt.target.closest('a') : null;
        if (anchor && anchor.href) {
          const isOutbound = anchor.host && anchor.host !== window.location.host;
          const isDownload = anchor.hasAttribute('download') || DOWNLOAD_EXT.test(anchor.href);
          if (isDownload && features.trackDownloads !== false) {
            track('download', { url: scrubUrl(anchor.href), text: (anchor.textContent || '').trim().slice(0, 120) }, { flush: true, immediate: true });
            return;
          }
          if (isOutbound && features.trackOutbound !== false) {
            track('outbound_link', { url: scrubUrl(anchor.href), host: anchor.host }, { flush: true, immediate: true });
            return;
          }
        }
        if (features.trackClicks === false) return;
        const el = evt.target;
        if (!el || !el.tagName) return;
        const trackable = el.closest ? el.closest('[data-track]') : null;
        if (trackable) {
          track(trackable.getAttribute('data-track') || 'click', safe(() => JSON.parse(trackable.getAttribute('data-track-props') || '{}'))() || {});
        }
      }, true);
    }

    // ---- scroll depth, reported at 25/50/75/100
    if (features.trackScroll !== false) {
      const reported = {};
      on(window, 'scroll', throttle(() => {
        const doc = document.documentElement;
        const height = Math.max(doc.scrollHeight, document.body.scrollHeight) - window.innerHeight;
        if (height <= 0) return;
        const depth = Math.min(100, Math.round((window.pageYOffset / height) * 100));
        state.maxScroll = Math.max(state.maxScroll, depth);
        [25, 50, 75, 100].forEach((mark) => {
          if (depth >= mark && !reported[mark]) {
            reported[mark] = true;
            track('scroll', { depth: mark });
          }
        });
      }, 500), { passive: true });
    }

    // ---- performance timings
    if (features.trackPerformance !== false) {
      on(window, 'load', () => {
        setTimeout(() => {
          const nav = safe(() => performance.getEntriesByType('navigation')[0])();
          if (!nav) return;
          track('performance', {
            ttfbMs: Math.round(nav.responseStart),
            domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
            loadMs: Math.round(nav.loadEventEnd),
            transferKb: Math.round((nav.transferSize || 0) / 1024),
          });
        }, 0);
      });
    }

    // ---- errors (opt-in)
    if (features.trackErrors === true) {
      on(window, 'error', (evt) => {
        track('error', { message: String(evt.message || '').slice(0, 500), source: evt.filename, line: evt.lineno });
      });
      on(window, 'unhandledrejection', (evt) => {
        track('exception', { message: String((evt.reason && evt.reason.message) || evt.reason || '').slice(0, 500) });
      });
    }

    // ---- visibility, tab close, refresh, browser close
    on(document, 'visibilitychange', () => {
      track('visibility', { state: document.visibilityState, maxScroll: state.maxScroll });
      if (document.visibilityState === 'hidden') state.transport.flush(context(), true);
    });

    // pagehide and beforeunload both fire on a normal unload; report once.
    let unloadReported = false;
    on(window, 'pageshow', () => { unloadReported = false; });
    const endSession = () => {
      if (unloadReported) return;
      unloadReported = true;
      track('session_end', {
        durationSeconds: Math.round((Date.now() - state.startedAt) / 1000),
        maxScroll: state.maxScroll,
      });
      state.transport.flush(context(), true);
    };
    // pagehide covers reload, tab close and bfcache; beforeunload is the fallback.
    on(window, 'pagehide', endSession);
    on(window, 'beforeunload', endSession);
  }

  // ------------------------------------------------------------------- init
  function loadConfig(apiHost, sdkKey) {
    const cached = storage.jsonGet(KEYS.config);
    if (cached && cached.sdkKey === sdkKey && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.config);
    }

    const opts = { credentials: 'omit', mode: 'cors' };
    let timer = null;
    if (typeof AbortController !== 'undefined') {
      const ctrl = new AbortController();
      opts.signal = ctrl.signal;
      timer = setTimeout(function () { try { ctrl.abort(); } catch (_) { /* ignore */ } }, 1500);
    }

    return fetch(apiHost + '/v1/sdk/config?sdkKey=' + encodeURIComponent(sdkKey), opts)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('config ' + r.status))))
      .then((body) => {
        const config = body.data || body;
        storage.jsonSet(KEYS.config, { sdkKey: sdkKey, config: config, expiresAt: Date.now() + 5 * 60 * 1000 });
        return config;
      })
      .catch(() => (cached ? cached.config : null))
      .then((config) => {
        if (timer) clearTimeout(timer);
        return config;
      });
  }

  function activate(apiHost, config) {
    state.active = true;
    state.visitorId = storage.durableGet(KEYS.visitor);
    const isNewVisitor = !state.visitorId;
    if (isNewVisitor) {
      state.visitorId = 'v_' + uuid().replace(/-/g, '');
      storage.durableSet(KEYS.visitor, state.visitorId);
    }
    state.userId = storage.lsGet(KEYS.user) || null;
    state.metadata = storage.jsonGet(KEYS.metadata) || {};

    const batch = (config && config.batch) || {};
    state.transport = createTransport({
      endpoint: apiHost + ((config && config.collectEndpoint) || '/v1/collect'),
      sdkKey: state.options.sdkKey,
      maxBatchSize: batch.maxSize || 20,
      flushIntervalMs: batch.flushIntervalMs || 5000,
      storage: storage,
      storageKey: KEYS.queue,
      getContext: context,
      onError: state.options.onError,
    });

    const session = ensureSession();
    persistFirstTouch();
    captureSessionTouch(session.isNew);
    refreshSessionTouchIfCampaign();
    state.lastPath = window.location.pathname + window.location.search;

    if (session.isNew) track('session_start', { isNewVisitor: isNewVisitor });
    // landing's url/referrer columns already hold the first-touch landing URL + referrer.
    if (isNewVisitor) track('landing', {});
    if (!config || !config.features || config.features.autoPageView !== false) {
      track('page_view', {});
    }

    bindAutomaticTracking();
    flushPendingIdentity();
    if (typeof state.options.onReady === 'function') safe(state.options.onReady)(api);
    markReady();
    return api;
  }

  function init(options) {
    try {
      if (state.initialized) return state.ready || Promise.resolve(api);
      state.initialized = true;
      state.options = options || {};
      state.ready = new Promise((resolve) => { state.resolveReady = resolve; });

      if (!state.options.sdkKey) {
        if (window.console) console.warn('[Tracker] sdkKey is required');
        markReady();
        return state.ready;
      }
      if (state.options.respectDoNotTrack !== false && navigator.doNotTrack === '1') {
        markReady();
        return state.ready;
      }

      const apiHost = (state.options.apiHost || '').replace(/\/$/, '') || inferHost();
      state.apiHost = apiHost;

      // Gate BEFORE any network: organic traffic pays zero cost and never
      // waits on the attribution host. Still watch SPA navigations in case
      // a later route introduces UTM (React Router / Next.js client nav).
      if (!shouldActivate()) {
        armLateActivation(apiHost);
        markReady();
        return state.ready;
      }

      return loadConfig(apiHost, state.options.sdkKey)
        .then((config) => {
          try {
            state.config = config;
            if (config && config.features && config.features.respectDoNotTrack && navigator.doNotTrack === '1') {
              markReady();
              return api;
            }
            if (!shouldActivate()) {
              armLateActivation(apiHost);
              markReady();
              return api;
            }
            return activate(apiHost, config);
          } catch (_) {
            state.active = false;
            markReady();
            return api;
          }
        })
        .catch(() => {
          markReady();
          return api;
        });
    } catch (_) {
      markReady();
      return state.ready || Promise.resolve(api);
    }
  }

  /**
   * React/Next SPAs often land organically then client-navigate onto a
   * campaign URL. Keep a cheap listener while idle so UTM can still activate
   * without a full page reload — still no network until that happens.
   */
  function armLateActivation(apiHost) {
    if (state.active || state.lateArmed) return;
    state.lateArmed = true;

    const attempt = safe(function lateActivate() {
      if (state.active || !state.initialized) return;
      if (!shouldActivate()) return;
      if (state.lateActivating) return;
      state.lateActivating = true;
      loadConfig(apiHost, state.options.sdkKey)
        .then((config) => {
          try {
            if (state.active) return;
            state.config = config;
            if (!shouldActivate()) return;
            activate(apiHost, config);
          } catch (_) { /* never break host */ }
        })
        .catch(() => { /* server down — host unaffected; retry on next nav */ })
        .then(() => { state.lateActivating = false; });
    });

    on(window, 'popstate', attempt);
    on(window, 'hashchange', attempt);
    ['pushState', 'replaceState'].forEach((method) => {
      try {
        const original = history[method];
        if (typeof original !== 'function' || original.__attrLatePatched) return;
        const patched = function patchedLateHistory() {
          const result = original.apply(this, arguments);
          setTimeout(attempt, 0);
          return result;
        };
        patched.__attrLatePatched = true;
        history[method] = patched;
      } catch (_) { /* ignore */ }
    });
  }

  function inferHost() {
    const script = document.currentScript || (function () {
      const scripts = document.getElementsByTagName('script');
      for (let i = scripts.length - 1; i >= 0; i -= 1) {
        if (/sdk(\.min)?\.js/.test(scripts[i].src)) return scripts[i];
      }
      return null;
    })();
    if (script && script.src) {
      const a = document.createElement('a');
      a.href = script.src;
      return a.protocol + '//' + a.host;
    }
    return window.location.origin;
  }

  function guard(fn) {
    return function guardedApi() {
      try { return fn.apply(this, arguments); } catch (_) { return null; }
    };
  }

  // -------------------------------------------------------------- public API
  const api = {
    init: init,

    identify: guard(function identify(userId, traits) {
      if (!userId) return null;
      setIdentity(userId, traits);
      // Local only until active — no network. One pending flag, no stacked handlers.
      if (!state.active) {
        state.pendingIdentify = true;
        return null;
      }
      return emitIdentify(traits);
    }),

    setUser: guard(function setUser(userId, traits) { return api.identify(userId, traits); }),

    unsetUser: guard(function unsetUser() {
      state.userId = null;
      storage.lsRemove(KEYS.user);
      return true;
    }),

    logout: guard(function logout(properties) {
      const id = track('logout', properties);
      api.unsetUser();
      return id;
    }),

    login: guard(function login(userId, properties) {
      if (userId) setIdentity(userId, properties);
      if (!state.active) {
        state.pendingLogin = true;
        return null;
      }
      return trackIdentityEvent('login', properties);
    }),

    register: guard(function register(userId, properties) {
      if (userId) setIdentity(userId, properties);
      if (!state.active) {
        state.pendingRegister = true;
        return null;
      }
      return trackIdentityEvent('register', properties);
    }),

    page: guard(function page(name, properties) {
      return track('page_view', Object.assign({ name: name }, properties || {}));
    }),

    event: guard(function event(name, properties) { return track(name, properties); }),
    custom: guard(function custom(name, properties) { return track(name, properties); }),
    goal: guard(function goal(name, properties) {
      return track('goal', Object.assign({ goal: name }, properties || {}));
    }),

    purchase: guard(function purchase(order) {
      const o = order || {};
      return track('purchase', {
        orderId: o.orderId, items: o.items, quantity: o.quantity, coupon: o.coupon,
      }, { revenue: o.revenue || o.amount || o.value, currency: o.currency, flush: true });
    }),

    trackRevenue: guard(function trackRevenue(amount, properties) {
      return track('revenue', properties, { revenue: amount, currency: (properties && properties.currency), flush: true });
    }),

    trackError: guard(function trackError(error, properties) {
      return track('error', Object.assign({
        message: String((error && error.message) || error || '').slice(0, 500),
        stack: String((error && error.stack) || '').slice(0, 1000),
      }, properties || {}));
    }),

    trackException: guard(function trackException(error, properties) { return api.trackError(error, properties); }),
    trackPerformance: guard(function trackPerformance(properties) { return track('performance', properties); }),
    trackTiming: guard(function trackTiming(name, durationMs, properties) {
      return track('timing', Object.assign({ name: name, durationMs: durationMs }, properties || {}));
    }),

    setMetadata: guard(function setMetadata(metadata) {
      state.metadata = Object.assign({}, state.metadata, metadata || {});
      storage.jsonSet(KEYS.metadata, state.metadata);
      return state.metadata;
    }),

    flush: guard(function flush() {
      return state.active && state.transport ? state.transport.flush(context(), false) : Promise.resolve(false);
    }),

    /** Header value to attach to your own API calls so backend actions join the journey. */
    getTrackerId: guard(function getTrackerId() {
      return state.visitorId ? state.visitorId + '.' + (state.sessionId || '') : null;
    }),

    getVisitorId: guard(function getVisitorId() { return state.visitorId; }),
    getSessionId: guard(function getSessionId() { return state.sessionId; }),
    isActive: guard(function isActive() { return state.active; }),

    /** Axios/fetch helper: attaches x-tracker-id to every outgoing request. */
    axiosInterceptor: guard(function axiosInterceptor(axiosInstance) {
      if (!axiosInstance || !axiosInstance.interceptors) return null;
      return axiosInstance.interceptors.request.use((cfg) => {
        const id = api.getTrackerId();
        if (id) {
          cfg.headers = cfg.headers || {};
          cfg.headers['x-tracker-id'] = id;
        }
        return cfg;
      });
    }),

    reset: guard(function reset() {
      state.listeners.forEach((off) => safe(off)());
      state.listeners = [];
      state.active = false;
      state.initialized = false;
      state.lateArmed = false;
      state.lateActivating = false;
      state.transport = null;
      state.config = null;
      state.options = {};
    }),
  };

  return api;
}

const Tracker = createTracker();

if (typeof window !== 'undefined') {
  // Replay anything queued before the script finished loading (React/Next stub).
  // init must settle before identify/login/event so calls aren't dropped while
  // loadConfig is still in flight.
  const pending = window.Tracker && window.Tracker.q;
  window.Tracker = Tracker;
  if (Array.isArray(pending) && pending.length) {
    const initCall = pending.find((call) => call && call[0] === 'init');
    const rest = pending.filter((call) => call && call[0] !== 'init');
    const replay = (call) => {
      const method = call[0];
      if (typeof Tracker[method] === 'function') safe(() => Tracker[method].apply(Tracker, call.slice(1)))();
    };
    const replayRest = () => rest.forEach(replay);
    if (initCall) {
      Promise.resolve(Tracker.init.apply(Tracker, initCall.slice(1))).then(replayRest).catch(replayRest);
    } else {
      replayRest();
    }
  }
}

module.exports = Tracker;
module.exports.createTracker = createTracker;
